import { SectionFooter } from "@admin-ui/components/application/section-footers/section-footer";
import { SectionHeader } from "@admin-ui/components/application/section-headers/section-headers";
import { SectionLabel } from "@admin-ui/components/application/section-headers/section-label";
import { NativeSelect } from "@admin-ui/components/base/select/select-native";
import { Button } from "@admin-ui/components/primitives/base/buttons/button";
import { Input } from "@admin-ui/components/primitives/base/input/input";
import type { FormEvent, ReactNode } from "react";
import { MapboxSearch, type MapboxSearchResult } from "@/components/admin/MapboxSearch";
import { NewPhotoSection } from "./components/new-photo-section";

export interface NewViewProps {
  name: string;
  countryCode: string;
  description: string;
  availableCountries: Array<{ iso_alpha2: string; name: string }>;
  /** Name of the location the form was pre-filled from, if any. */
  fromLocationName?: string;
  /** True once a place has been selected; gates the editable fields and submit. */
  hasSelection: boolean;
  /** True while the create request is in flight. */
  processing: boolean;
  /** Href for the cancel buttons. */
  cancelHref: string;
  /** Endpoint the Mapbox place search posts to. */
  mapboxSearchPath: string;
  /** Pre-wired map slot element (owns the map host ref). */
  map: ReactNode;
  onMapboxSelect: (result: MapboxSearchResult) => void;
  onNameChange: (value: string) => void;
  onCountryChange: (value: string) => void;
  onDescriptionChange: (value: string) => void;
  onPhotoChange: (signedId: string, stockPhotoUrl: string) => void;
  onSubmit: (event: FormEvent) => void;
}

export function NewView({
  name,
  countryCode,
  description,
  availableCountries,
  fromLocationName,
  hasSelection,
  processing,
  cancelHref,
  mapboxSearchPath,
  map,
  onMapboxSelect,
  onNameChange,
  onCountryChange,
  onDescriptionChange,
  onPhotoChange,
  onSubmit,
}: NewViewProps) {
  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-6">
      <SectionHeader.Root>
        <SectionHeader.Group>
          <div className="flex flex-1 flex-col justify-center gap-0.5 self-stretch">
            <SectionHeader.Heading>Destination details</SectionHeader.Heading>
            <SectionHeader.Subheading>
              Search for a place to get started, then adjust the details below.
            </SectionHeader.Subheading>
          </div>
          <SectionHeader.Actions>
            <Button color="secondary" size="sm" href={cancelHref}>
              Cancel
            </Button>
            <Button type="submit" color="primary" size="sm" isDisabled={processing || !hasSelection}>
              Create
            </Button>
          </SectionHeader.Actions>
        </SectionHeader.Group>
      </SectionHeader.Root>

      <div className="flex flex-col gap-5">
        {/* Place search */}
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-[minmax(200px,280px)_minmax(400px,512px)] lg:gap-8">
          <SectionLabel.Root
            isRequired
            size="sm"
            title="Place"
            description="Search Mapbox to auto-populate fields below."
          />
          <div className="flex flex-col gap-2">
            <MapboxSearch searchPath={mapboxSearchPath} onSelect={onMapboxSelect} />
            {fromLocationName && (
              <p className="text-sm text-tertiary">
                Pre-filled from <span className="font-medium">{fromLocationName}</span>. You can search to override.
              </p>
            )}
          </div>
        </div>

        <hr className="h-px w-full border-none bg-border-secondary" />

        {/* Name */}
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(200px,280px)_minmax(400px,512px)] lg:gap-8">
          <SectionLabel.Root isRequired size="sm" title="Name" className="max-lg:hidden" />
          <Input
            label="Name"
            value={name}
            onChange={onNameChange}
            isRequired
            isDisabled={!hasSelection}
            className="lg:label:hidden"
          />
        </div>

        <hr className="h-px w-full border-none bg-border-secondary" />

        <div className="grid grid-cols-1 lg:grid-cols-[minmax(200px,280px)_minmax(400px,512px)] lg:gap-8">
          <SectionLabel.Root
            isRequired
            size="sm"
            title="Country"
            description="The country this destination belongs to"
            className="max-lg:hidden"
          />
          <NativeSelect
            label="Country"
            value={countryCode}
            onChange={(event) => onCountryChange(event.target.value)}
            options={availableCountries.map((country) => ({
              value: country.iso_alpha2,
              label: country.name,
            }))}
            disabled={!hasSelection}
            className="lg:label:hidden"
          />
        </div>

        <hr className="h-px w-full border-none bg-border-secondary" />

        {/* Description */}
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-[minmax(200px,280px)_minmax(400px,512px)] lg:gap-8">
          <SectionLabel.Root size="sm" title="Description" description="Brief description of this destination." />
          <textarea
            value={description}
            onChange={(e) => onDescriptionChange(e.target.value)}
            disabled={!hasSelection}
            className="w-full rounded-lg border border-primary px-3.5 py-2.5 text-sm text-primary shadow-xs placeholder:text-placeholder focus:border-brand focus:outline-none focus:ring-4 focus:ring-brand/12 disabled:cursor-not-allowed disabled:opacity-50"
            rows={3}
            placeholder="Optional description"
          />
        </div>

        <hr className="h-px w-full border-none bg-border-secondary" />

        <div className="grid grid-cols-1 gap-5 lg:grid-cols-[minmax(200px,280px)_minmax(400px,512px)] lg:gap-8">
          <SectionLabel.Root size="sm" title="Photo" description="Cover photo for this destination." />
          <div className={!hasSelection ? "pointer-events-none opacity-50" : undefined}>
            <NewPhotoSection onPhotoChange={onPhotoChange} />
          </div>
        </div>

        <hr className="h-px w-full border-none bg-border-secondary" />

        {/* Bounding box */}
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-[minmax(200px,280px)_1fr] lg:gap-8">
          <SectionLabel.Root
            size="sm"
            title="Bounding box"
            description="Drag the corners to adjust the area. Locations inside this boundary are automatically matched."
          />
          <div className={!hasSelection ? "pointer-events-none opacity-50" : undefined}>{map}</div>
        </div>
      </div>

      <SectionFooter.Root>
        <SectionFooter.Actions>
          <Button color="secondary" size="sm" href={cancelHref}>
            Cancel
          </Button>
          <Button type="submit" color="primary" size="sm" isDisabled={processing || !hasSelection}>
            Create
          </Button>
        </SectionFooter.Actions>
      </SectionFooter.Root>
    </form>
  );
}
